// Ported from files/snapdraw-design-kit/code/src/lib/i18n/strings/help.ts
// (Lovable reference implementation) — backlog UX-10. `start.*` keys back
// the Dashboard's GettingStarted checklist; `help.*` keys back the /help
// reference page.
export const en = {
  // ---- Getting-started checklist (Dashboard) -------------------------------
  "start.title": "Getting started",
  "start.body": "Four short jobs. Do them in any order — this card disappears once they're done.",
  "start.progress": "{done} of {total} done",
  "start.expand": "Show steps",
  "start.collapse": "Hide steps",
  "start.learn": "Learn how this works",
  "start.done": "Done",
  "start.todo": "To do",

  "start.profile.title": "Add your business details",
  "start.profile.body": "Name, license and address — these print on every quote and pay application.",
  "start.client.title": "Add your first client",
  "start.client.body": "The GC or owner you bill. Saved once, reused on every document.",
  "start.quote.title": "Build your first quote",
  "start.quote.body": "Line items, totals and payment terms. Send it as a PDF.",
  "start.payapp.title": "Try a G702 pay application",
  "start.payapp.body": "A G702 cover sheet with G703 continuation lines, billed by period.",

  // ---- Getting Started reference page --------------------------------------
  "help.nav": "Help",
  "help.breadcrumb": "Help · Getting started",
  "help.eyebrow": "Getting started",
  "help.title": "How SnapDraw works",
  "help.intro":
    "A short read for your first week. The core flow is a quote that becomes a pay application — everything else exists to make those two faster.",
  "help.contents": "Contents",
  "help.support": "Still stuck? Email us.",

  "help.flow.title": "The core flow",
  "help.flow.p1":
    "You price the work in a quote. When the client accepts, you bill against it — either as a single invoice for small jobs, or as a running G702/G703 pay application for jobs billed month to month.",
  "help.flow.p2":
    "Nothing is ever retyped. A quote carries its line items forward into the invoice or the pay application, and each pay application carries forward what you already billed.",
  "help.flow.s1": "New Document → pick Quote → add line items → send the PDF.",
  "help.flow.s2": "Client accepts → mark it Won.",
  "help.flow.s3": "Bill it → Invoice for one-shot jobs, G702/G703 for phased work.",

  "help.status.title": "What the statuses mean",
  "help.status.draft": "Draft — only you can see it. Nothing has left the building.",
  "help.status.sent": "Sent — the PDF went out. The clock on your payment terms starts here.",
  "help.status.won": "Won — the client accepted the quote. Ready to bill.",
  "help.status.paid": "Paid — money received; you recorded the date.",
  "help.status.overdue": "Overdue — past the due date on an invoice or pay application. Shown in amber everywhere.",
  "help.status.note":
    "Quotes show payment terms only (\"Net 30\"), never a computed due date — the clock doesn't start until you bill.",

  "help.quote.title": "Quotes",
  "help.quote.p1":
    "A quote is a priced scope. Add line items by hand or pull them from your Rate Catalog, set quantity and rate, and SnapDraw totals it with tax.",
  "help.quote.p2":
    "Validity is how long your price holds — 30 days is typical. Payment terms (Net 15/30/60) travel with the quote into whatever you bill from it.",
  "help.quote.p3":
    "Need to re-price after a change? Revise the quote instead of editing it. The original is kept and the trail shows Original → Revision → Invoice.",

  "help.photos.title": "Photos on a quote",
  "help.photos.p1":
    "The Photos card in the quote builder holds up to 8 job-site photos — existing conditions, before shots, the meter you found corroded. Add them from your camera roll or take them on the spot.",
  "help.photos.p2":
    "Every photo you attach prints on the PDF your client receives, in the order you set, with an optional caption under each one. The PRINTS ON THE PDF badge is the promise: nothing in that card is editor-only.",
  "help.photos.note":
    "Reorder with the up/down arrows before you send — photo 1 prints first. Photos attach to quotes only, not invoices or pay applications.",

  "help.aia.title": "G702 / G703 pay applications",
  "help.aia.p1":
    "This is the AIA-style form GCs ask for on larger jobs. The G703 continuation sheet lists every scheduled value — your contract broken into line items. The G702 cover sheet is the summary the GC signs.",
  "help.aia.p2":
    "Each period you enter what you completed. SnapDraw computes work completed to date, materials stored, retainage, total earned less retainage, less previous applications, and the current payment due.",
  "help.aia.p3":
    "Retainage is the percentage the GC holds back until the job closes — usually 5% or 10%. Enter it once on the contract and every application applies it.",
  "help.aia.p4":
    "Application numbers run in sequence off the contract, so App #4 already knows what #1–#3 billed.",

  "help.areas.title": "The rest of the app",
  "help.areas.clients": "Clients — the GCs and owners you bill. Saved once, picked from a list on every document.",
  "help.areas.catalog": "Rate Catalog — your standard labor, material and equipment rates. Drop them into a quote instead of retyping.",
  "help.areas.contracts": "Contracts — the parent record for phased work. Holds the scheduled values, retainage and the ledger of pay applications billed so far.",
  "help.areas.reports": "Reports — outstanding balance, A/R aging and what's overdue.",
  "help.areas.templates": "Templates — save a finished document's line items and reuse it on the next job.",
  "help.areas.account": "Account — business details, language, light/dark theme, plan and billing.",

  "help.field.title": "On a phone",
  "help.field.p1":
    "Every screen works on a phone. The bottom bar carries Home, Documents, Clients, Catalog, Reports, Contracts and Account — this page is reachable by link instead, so the bar stays uncrowded.",
  "help.field.p2":
    "SnapDraw needs a connection — there is no offline mode yet. Build the document where you have signal.",

  "help.pdf.title": "Sending and signatures",
  "help.pdf.p1":
    "Everything sends as a PDF you can email or hand over. Documents print with a signature line for a physical signature — there is no built-in e-signature, and payments are not collected in the app.",
} as const;

export const es: Record<keyof typeof en, string> = {
  "start.title": "Primeros pasos",
  "start.body": "Cuatro tareas cortas. Hazlas en cualquier orden — esta tarjeta desaparece al terminarlas.",
  "start.progress": "{done} de {total} listas",
  "start.expand": "Ver pasos",
  "start.collapse": "Ocultar pasos",
  "start.learn": "Aprende cómo funciona",
  "start.done": "Listo",
  "start.todo": "Pendiente",

  "start.profile.title": "Agrega los datos de tu negocio",
  "start.profile.body": "Nombre, licencia y dirección — se imprimen en cada cotización y solicitud de pago.",
  "start.client.title": "Agrega tu primer cliente",
  "start.client.body": "El contratista general o el dueño a quien facturas. Se guarda una vez y se reutiliza.",
  "start.quote.title": "Crea tu primera cotización",
  "start.quote.body": "Partidas, totales y términos de pago. Envíala en PDF.",
  "start.payapp.title": "Prueba una solicitud de pago G702",
  "start.payapp.body": "Una portada G702 con partidas G703, facturada por periodo.",

  "help.nav": "Ayuda",
  "help.breadcrumb": "Ayuda · Primeros pasos",
  "help.eyebrow": "Primeros pasos",
  "help.title": "Cómo funciona SnapDraw",
  "help.intro":
    "Una lectura corta para tu primera semana. El flujo central es una cotización que se convierte en solicitud de pago — todo lo demás existe para hacer esos dos pasos más rápidos.",
  "help.contents": "Contenido",
  "help.support": "¿Sigues atorado? Escríbenos.",

  "help.flow.title": "El flujo central",
  "help.flow.p1":
    "Cotizas el trabajo. Cuando el cliente acepta, facturas contra esa cotización — con una factura única en trabajos pequeños, o con una solicitud de pago G702/G703 en obras que se facturan mes a mes.",
  "help.flow.p2":
    "Nada se vuelve a escribir. La cotización lleva sus partidas a la factura o a la solicitud de pago, y cada solicitud arrastra lo ya facturado.",
  "help.flow.s1": "Nuevo documento → elige Cotización → agrega partidas → envía el PDF.",
  "help.flow.s2": "El cliente acepta → márcala como Ganada.",
  "help.flow.s3": "Factúrala → Factura para trabajos únicos, G702/G703 para obra por fases.",

  "help.status.title": "Qué significan los estados",
  "help.status.draft": "Borrador — solo tú lo ves. Nada ha salido.",
  "help.status.sent": "Enviado — el PDF salió. Aquí empieza el reloj de tus términos de pago.",
  "help.status.won": "Ganado — el cliente aceptó la cotización. Lista para facturar.",
  "help.status.paid": "Pagado — recibiste el dinero y registraste la fecha.",
  "help.status.overdue": "Vencido — pasó la fecha de vencimiento de una factura o solicitud. Se muestra en ámbar.",
  "help.status.note":
    "Las cotizaciones muestran solo los términos de pago (\"Net 30\"), nunca una fecha calculada — el reloj no corre hasta que facturas.",

  "help.quote.title": "Cotizaciones",
  "help.quote.p1":
    "Una cotización es un alcance con precio. Agrega partidas a mano o tómalas de tu Catálogo de tarifas, pon cantidad y tarifa, y SnapDraw calcula el total con impuesto.",
  "help.quote.p2":
    "La vigencia es cuánto tiempo se mantiene tu precio — 30 días es lo normal. Los términos de pago (Net 15/30/60) viajan con la cotización a lo que factures después.",
  "help.quote.p3":
    "¿Cambió el alcance? Revisa la cotización en vez de editarla. Se conserva la original y el rastro muestra Original → Revisión → Factura.",

  "help.photos.title": "Fotos en una cotización",
  "help.photos.p1":
    "La tarjeta Fotos en el editor de cotizaciones guarda hasta 8 fotos de obra — condiciones existentes, fotos de antes, el medidor corroído que encontraste. Agrégalas de tu galería o tómalas en el momento.",
  "help.photos.p2":
    "Cada foto que adjuntas se imprime en el PDF que recibe tu cliente, en el orden que eliges, con una leyenda opcional debajo. El sello SE IMPRIME EN EL PDF es la promesa: nada en esa tarjeta es solo de edición.",
  "help.photos.note":
    "Reordena con las flechas antes de enviar — la foto 1 se imprime primero. Las fotos se adjuntan solo a cotizaciones, no a facturas ni solicitudes de pago.",

  "help.aia.title": "Solicitudes de pago G702 / G703",
  "help.aia.p1":
    "Es el formato tipo AIA que piden los contratistas generales en obras grandes. La hoja G703 lista cada valor programado — tu contrato desglosado en partidas. La portada G702 es el resumen que se firma.",
  "help.aia.p2":
    "Cada periodo capturas lo completado. SnapDraw calcula el trabajo completado a la fecha, materiales almacenados, retención, total ganado menos retención, menos solicitudes anteriores y el pago actual.",
  "help.aia.p3":
    "La retención es el porcentaje que el contratista retiene hasta cerrar la obra — normalmente 5% o 10%. Se captura una vez en el contrato y se aplica en cada solicitud.",
  "help.aia.p4":
    "Los números de solicitud siguen la secuencia del contrato, así que la #4 ya sabe qué facturaron la #1 a la #3.",

  "help.areas.title": "El resto de la app",
  "help.areas.clients": "Clientes — los contratistas y dueños a quienes facturas. Se guardan una vez y se eligen de una lista.",
  "help.areas.catalog": "Catálogo de tarifas — tus tarifas de mano de obra, material y equipo. Insértalas sin volver a escribirlas.",
  "help.areas.contracts": "Contratos — el registro padre de obra por fases. Guarda los valores programados, la retención y el historial de solicitudes.",
  "help.areas.reports": "Reportes — saldo pendiente, antigüedad de cuentas por cobrar y lo vencido.",
  "help.areas.templates": "Plantillas — guarda las partidas de un documento terminado y reutilízalas en el siguiente trabajo.",
  "help.areas.account": "Cuenta — datos del negocio, idioma, tema claro/oscuro, plan y facturación.",

  "help.field.title": "En el teléfono",
  "help.field.p1":
    "Todas las pantallas funcionan en el teléfono. La barra inferior lleva Inicio, Documentos, Clientes, Catálogo, Reportes, Contratos y Cuenta — esta página se abre por enlace, para no saturar la barra.",
  "help.field.p2":
    "SnapDraw necesita conexión — todavía no hay modo sin conexión. Arma el documento donde tengas señal.",

  "help.pdf.title": "Envíos y firmas",
  "help.pdf.p1":
    "Todo se envía como PDF que puedes mandar por correo o entregar en mano. Los documentos se imprimen con una línea de firma física — no hay firma electrónica integrada y no se cobran pagos dentro de la app.",
};
